import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Download, FileText, Shield, Trash2, Key, Activity, AlertTriangle, Zap, Brain, DollarSign } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = ({ token, onLogout }) => {
  const [activityLogs, setActivityLogs] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [aiUsage, setAiUsage] = useState(null);
  const [categorizationRules, setCategorizationRules] = useState([]);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showClearDataDialog, setShowClearDataDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchActivityLogs();
    fetchSubscriptionStatus();
    fetchAIUsage();
    fetchCategorizationRules();
  }, []);

  const fetchCategorizationRules = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/categorization-rules`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategorizationRules(response.data);
    } catch (error) {
      console.error('Error fetching categorization rules:', error);
    }
  };

  const deleteRule = async (ruleId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/categorization-rules/${ruleId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Rule deleted');
      fetchCategorizationRules();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const fetchAIUsage = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/ai-usage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAiUsage(response.data);
    } catch (error) {
      // Not admin or no access - that's fine
      console.log('AI usage not available (admin only)');
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const fetchActivityLogs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/user/activity-logs?limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActivityLogs(response.data);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    }
  };

  const handleDownloadBackup = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/user/backup`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `finance_backup_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Backup downloaded successfully');
    } catch (error) {
      toast.error('Failed to download backup');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/user/export-csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transactions_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Transactions exported to CSV');
    } catch (error) {
      toast.error('Failed to export transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleClearAllData = async () => {
    try {
      setLoading(true);
      const response = await axios.delete(`${BACKEND_URL}/api/transactions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(response.data.message);
      setShowClearDataDialog(false);
    } catch (error) {
      toast.error('Failed to clear data');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      setLoading(true);
      await axios.post(
        `${BACKEND_URL}/api/user/change-password`,
        null,
        {
          params: {
            old_password: passwordData.oldPassword,
            new_password: passwordData.newPassword,
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success('Password changed successfully');
      setShowPasswordDialog(false);
      setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setLoading(true);
      await axios.delete(`${BACKEND_URL}/api/user/account`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('Account deleted successfully');
      setTimeout(() => {
        onLogout();
      }, 2000);
    } catch (error) {
      toast.error('Failed to delete account');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    if (action.includes('LOGIN')) return '🔐';
    if (action.includes('BACKUP')) return '💾';
    if (action.includes('EXPORT')) return '📥';
    if (action.includes('PASSWORD')) return '🔑';
    if (action.includes('DELETE')) return '🗑️';
    return '📝';
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight text-foreground">
          Settings & Security
        </h1>
        <p className="mt-1 text-base font-body text-muted-foreground">
          Manage your account, data, and security preferences
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-sm shadow-sm p-6" data-testid="subscription-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-sm bg-purple-50 flex items-center justify-center">
              <Zap className="h-5 w-5 text-purple-600" />
            </div>
            <h2 className="text-2xl font-heading font-medium">Subscription</h2>
          </div>
          {subscriptionStatus ? (
            <div className="space-y-4">
              <div className="p-4 bg-muted/50 border border-border rounded-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-body text-muted-foreground">Current Plan</span>
                  <span className={`text-xs font-mono px-2 py-1 rounded-sm ${
                    subscriptionStatus.is_promotional ? 'bg-green-100 text-green-700' :
                    subscriptionStatus.plan === 'free' ? 'bg-gray-100 text-gray-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {subscriptionStatus.is_promotional ? 'PROMO FREE' :
                     subscriptionStatus.plan === 'free' ? 'FREE' :
                     subscriptionStatus.plan.toUpperCase().replace(/_/g, ' ')}
                  </span>
                </div>
                {subscriptionStatus.is_promotional && subscriptionStatus.expires_at && (
                  <p className="text-xs font-body text-green-600">
                    Free access until {new Date(subscriptionStatus.expires_at).toLocaleDateString()}
                  </p>
                )}
                {!subscriptionStatus.is_promotional && subscriptionStatus.plan !== 'free' && subscriptionStatus.expires_at && (
                  <p className="text-xs font-body text-muted-foreground">
                    Renews on {new Date(subscriptionStatus.expires_at).toLocaleDateString()}
                  </p>
                )}
              </div>
              
              {subscriptionStatus.plan === 'free' && !subscriptionStatus.is_promotional && (
                <div className="space-y-2">
                  <p className="text-sm font-body text-muted-foreground">
                    Upgrade to unlock premium features:
                  </p>
                  <ul className="text-xs font-body text-muted-foreground space-y-1 ml-4">
                    <li>• Unlimited transactions</li>
                    <li>• AI categorization</li>
                    <li>• Advanced insights</li>
                  </ul>
                  <Button
                    onClick={() => window.location.href = '/pricing'}
                    className="w-full mt-3"
                    data-testid="upgrade-button"
                  >
                    <Zap className="h-4 w-4 mr-2" />
                    Upgrade Now
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Loading subscription info...</p>
          )}
        </div>

        <div className="bg-card border border-border rounded-sm shadow-sm p-6" data-testid="data-backup-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-sm bg-blue-50 flex items-center justify-center">
              <Download className="h-5 w-5 text-blue-600" />
            </div>
            <h2 className="text-2xl font-heading font-medium">Data Management</h2>
          </div>
          <p className="text-sm font-body text-muted-foreground mb-4">
            Download your complete financial data or clear all transactions to start fresh.
          </p>
          <div className="space-y-2">
            <Button
              onClick={handleDownloadBackup}
              disabled={loading}
              className="w-full"
              data-testid="download-backup-button"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Full Backup (JSON)
            </Button>
            <Button
              onClick={handleExportCSV}
              disabled={loading}
              variant="outline"
              className="w-full"
              data-testid="export-csv-button"
            >
              <FileText className="h-4 w-4 mr-2" />
              Export Transactions (CSV)
            </Button>
            <Button
              onClick={() => setShowClearDataDialog(true)}
              disabled={loading}
              variant="outline"
              className="w-full border-orange-300 text-orange-600 hover:bg-orange-50"
              data-testid="clear-all-data-button"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear All Transactions
            </Button>
          </div>
        </div>

        <div className="bg-card border border-border rounded-sm shadow-sm p-6" data-testid="security-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-sm bg-green-50 flex items-center justify-center">
              <Shield className="h-5 w-5 text-green-600" />
            </div>
            <h2 className="text-2xl font-heading font-medium">Security</h2>
          </div>
          <p className="text-sm font-body text-muted-foreground mb-4">
            Manage your password and account security settings.
          </p>
          <Button
            onClick={() => setShowPasswordDialog(true)}
            variant="outline"
            className="w-full"
            data-testid="change-password-button"
          >
            <Key className="h-4 w-4 mr-2" />
            Change Password
          </Button>
        </div>
      </div>

      {/* Categorization Rules Section */}
      <div className="bg-card border border-border rounded-sm shadow-sm p-6 mt-6" data-testid="categorization-rules-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-sm bg-amber-50 flex items-center justify-center">
            <Zap className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-2xl font-heading font-medium">Auto-Categorization Rules</h2>
            <p className="text-sm font-body text-muted-foreground">
              These rules automatically categorize transactions during import based on keywords in the description.
            </p>
          </div>
        </div>
        
        {categorizationRules.length === 0 ? (
          <div className="bg-muted/50 rounded-sm p-4 text-center">
            <p className="text-sm text-muted-foreground">
              No custom rules yet. When you use "Update Similar" on the Transactions page and check "Save as rule for future imports", your rules will appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-4 gap-4 text-xs font-mono text-muted-foreground uppercase px-3 pb-2 border-b">
              <span>Keyword</span>
              <span>Category</span>
              <span>Type</span>
              <span>Actions</span>
            </div>
            {categorizationRules.map((rule) => (
              <div key={rule.id} className="grid grid-cols-4 gap-4 items-center px-3 py-2 rounded-sm hover:bg-muted/50" data-testid={`rule-${rule.id}`}>
                <span className="font-medium text-sm truncate" title={rule.keyword}>"{rule.keyword}"</span>
                <span className="text-sm text-muted-foreground">{rule.category_name}</span>
                <span className={`text-xs px-2 py-1 rounded-sm w-fit ${
                  rule.transaction_type === 'income' ? 'bg-green-100 text-green-700' :
                  rule.transaction_type === 'expense' ? 'bg-red-100 text-red-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {rule.transaction_type}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteRule(rule.id)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 w-fit"
                  data-testid={`delete-rule-${rule.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI Usage Section - Admin Only */}
      {aiUsage && (
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-sm shadow-sm p-6" data-testid="ai-usage-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-sm bg-purple-100 flex items-center justify-center">
              <Brain className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-2xl font-heading font-medium">AI Usage Tracking</h2>
              <p className="text-xs text-muted-foreground">Monitor AI costs across all users</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-sm p-4 border border-purple-100">
              <p className="text-xs font-mono text-muted-foreground uppercase">Total AI Calls</p>
              <p className="text-2xl font-bold text-purple-600">{aiUsage.total_calls}</p>
            </div>
            <div className="bg-white rounded-sm p-4 border border-purple-100">
              <p className="text-xs font-mono text-muted-foreground uppercase">Est. Tokens Used</p>
              <p className="text-2xl font-bold text-blue-600">{aiUsage.total_tokens_estimated?.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-sm p-4 border border-purple-100">
              <p className="text-xs font-mono text-muted-foreground uppercase">Est. Cost (USD)</p>
              <p className="text-2xl font-bold text-green-600">${aiUsage.total_cost_estimated?.toFixed(4)}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-sm p-4 border border-purple-100">
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <Zap className="h-4 w-4 text-yellow-500" />
                Usage by Feature
              </h3>
              <div className="space-y-2 text-sm">
                {Object.entries(aiUsage.usage_by_feature || {}).map(([feature, data]) => (
                  <div key={feature} className="flex justify-between">
                    <span className="text-muted-foreground">{feature}</span>
                    <span className="font-mono">{data.calls} calls (${data.cost?.toFixed(4)})</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-white rounded-sm p-4 border border-purple-100">
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-green-500" />
                Usage by User
              </h3>
              <div className="space-y-2 text-sm max-h-40 overflow-y-auto">
                {Object.entries(aiUsage.usage_by_user || {}).map(([userId, data]) => (
                  <div key={userId} className="flex justify-between">
                    <span className="text-muted-foreground truncate max-w-[150px]" title={userId}>
                      {userId.slice(0, 8)}...
                    </span>
                    <span className="font-mono">{data.calls} calls (${data.cost?.toFixed(4)})</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-sm shadow-sm p-6" data-testid="activity-log-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-sm bg-purple-50 flex items-center justify-center">
            <Activity className="h-5 w-5 text-purple-600" />
          </div>
          <h2 className="text-2xl font-heading font-medium">Recent Activity</h2>
        </div>
        <div className="space-y-2">
          {activityLogs.length === 0 ? (
            <p className="text-center text-muted-foreground font-body py-8">No activity logs yet</p>
          ) : (
            activityLogs.map((log) => (
              <div
                key={log.id}
                className="flex items-start gap-3 p-3 border border-border rounded-sm hover:bg-muted/50 transition-colors"
                data-testid="activity-log-item"
              >
                <span className="text-xl mt-0.5">{getActionIcon(log.action)}</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-body font-medium text-sm text-foreground">{log.action.replace(/_/g, ' ')}</p>
                    <p className="text-xs font-mono text-muted-foreground">
                      {new Date(log.timestamp).toLocaleDateString()} {new Date(log.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                  <p className="text-xs font-body text-muted-foreground mt-1">{log.details}</p>
                  {log.ip_address && (
                    <p className="text-xs font-mono text-muted-foreground mt-1">IP: {log.ip_address}</p>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="bg-card border border-red-200 rounded-sm shadow-sm p-6" data-testid="danger-zone">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-sm bg-red-50 flex items-center justify-center">
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>
          <h2 className="text-2xl font-heading font-medium text-red-900">Danger Zone</h2>
        </div>
        <p className="text-sm font-body text-muted-foreground mb-4">
          Permanently delete your account and all associated data. This action cannot be undone.
        </p>
        <Button
          onClick={() => setShowDeleteDialog(true)}
          variant="destructive"
          data-testid="delete-account-button"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Delete Account
        </Button>
      </div>

      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent data-testid="password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Change Password</DialogTitle>
            <DialogDescription className="font-body">
              Enter your current password and choose a new one
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label htmlFor="oldPassword" className="font-body text-sm font-medium">
                Current Password
              </Label>
              <Input
                id="oldPassword"
                type="password"
                value={passwordData.oldPassword}
                onChange={(e) => setPasswordData({ ...passwordData, oldPassword: e.target.value })}
                required
                className="mt-1"
                data-testid="old-password-input"
              />
            </div>

            <div>
              <Label htmlFor="newPassword" className="font-body text-sm font-medium">
                New Password
              </Label>
              <Input
                id="newPassword"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                required
                minLength={6}
                className="mt-1"
                data-testid="new-password-input"
              />
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="font-body text-sm font-medium">
                Confirm New Password
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                required
                className="mt-1"
                data-testid="confirm-password-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowPasswordDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} data-testid="save-password-button">
                {loading ? 'Changing...' : 'Change Password'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-red-900">Delete Account</DialogTitle>
            <DialogDescription className="font-body">
              This will permanently delete your account and all data. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-red-50 border border-red-200 rounded-sm p-4">
            <p className="font-body text-sm text-red-900">
              <strong>Warning:</strong> You will lose:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-sm font-body text-red-800">
              <li>All transactions and financial data</li>
              <li>All categories and budgets</li>
              <li>Activity logs and account history</li>
            </ul>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={loading}
              data-testid="confirm-delete-button"
            >
              {loading ? 'Deleting...' : 'Permanently Delete Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showClearDataDialog} onOpenChange={setShowClearDataDialog}>
        <DialogContent data-testid="clear-data-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-orange-900">Clear All Transactions</DialogTitle>
            <DialogDescription className="font-body">
              This will delete all your transactions. Your account, categories, and settings will be preserved.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-orange-50 border border-orange-200 rounded-sm p-4">
            <p className="font-body text-sm text-orange-900">
              <strong>Note:</strong> This is useful if you want to:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-sm font-body text-orange-800">
              <li>Start fresh with new data</li>
              <li>Re-import transactions to test new features</li>
              <li>Clear test data before going live</li>
            </ul>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowClearDataDialog(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-orange-600 hover:bg-orange-700"
              onClick={handleClearAllData}
              disabled={loading}
              data-testid="confirm-clear-data-button"
            >
              {loading ? 'Clearing...' : 'Clear All Transactions'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
